/* eslint-disable @typescript-eslint/no-unused-vars */
//DBTables
import { useCallback, useMemo, useState } from "react";
import { DBModule, ModuleTypeFamily, ModuleTypeId } from "../../types/DBTables";
import { post } from "../../modules/server";
import Notify from "devextreme/ui/notify";
import { useForm } from "react-hook-form";

import ValidationSummaryMessages from "../Configuration/ValidationSummaryMessages";
import ModuleEditor from "../Configuration/ModuleEditor";
import ModuleRoundEditor from "./ModuleRoundEditor";
import { IModuleRound, IRoundHandler, useRound } from "../../modules/roundHandler";
import { ModuleType } from "../../types/module-type";
import { Card } from "react-bootstrap";
import { nameof } from "../Global/Utils";
import { IModuleLokaler, IModuleVentilationFTXochFTConfig, IModuleVärmePrimärPump, IModuleVärmePrimärPumpConfig } from "../../types/ModuleDatas";
import { IJ3_FTX_FT_BFData, IJ3_FX_F_BFData } from "../../types/BFDatas";
import { logDebugSilently } from "../../modules/logger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useGlobalData } from "../GlobalDataProvider";
import { Colors } from "../../modules/Colors";

interface IProps {
  module: IModuleRound;
  onCancel: () => void;
  onSubmit: (data: any, hasChanges: boolean) => void;
  onSaveDraft: (data: any, hasChanges: boolean) => void;
}

const getModuleTypeDisplayName = (id: ModuleTypeId) => {
  switch (id) {
    case ModuleTypeId.Byggnadsdata:
      return "";
    case ModuleTypeId.Lokaler:
      return "";
    case ModuleTypeId.VarmePrimarFjarr:
      return "Fjärrvärme primärsystem";
    case ModuleTypeId.VarmePrimarPump:
      return "Värmepump primärvärmesystem";
    case ModuleTypeId.VarmeSekundarVatten:
      return "Vattenburet sekundärvärmesystem i byggnaden";
    case ModuleTypeId.VarmeElVarmepump:
      return "Värmepump Luft / Luft";
    case ModuleTypeId.VarmeElStralningsvarmare:
      return "Strålningsvärmare";
    case ModuleTypeId.VarmeElRadiatorer:
      return "Elradiatorer";
    case ModuleTypeId.VarmeElAirotemper:
      return "EL airotemper";
    case ModuleTypeId.VarmeElGolvvarme:
      return "Elektrisk golvvärme";
    case ModuleTypeId.VentilationS:
      return "Ventilation (S-system)";
    case ModuleTypeId.VentilationF:
      return "Ventilation (F-system)";
    case ModuleTypeId.VentilationFT:
      return "Ventilation (FT-system)";
    case ModuleTypeId.VentilationFX:
      return "Ventilation (FX-system)";
    case ModuleTypeId.VentilationFTX:
      return "Ventilation (FTX-system)";
    case ModuleTypeId.KylaVattenburet:
      return "Vattenburet kylsystem";
    case ModuleTypeId.KylaMaskin:
      return "Kylmaskin";
    default:
      return "?";
  }
};

const validateModuleData = (id: ModuleTypeId, moduleData: any, roundHandler: IRoundHandler, module: IModuleRound) => {
  switch (id) {
    case ModuleTypeId.VentilationF:
    case ModuleTypeId.VentilationFX:
    case ModuleTypeId.VentilationFT:
    case ModuleTypeId.VentilationFTX:
      if (!moduleData.tidsKanaler) {
        moduleData.tidsKanaler = {
          ventilation: { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] },
          närvaro: { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] },
        };
        const lastRound = roundHandler.getLastFinishedRound();
        if (lastRound) {
          const lastRoundModule = lastRound.data.modules.find((m) => m.id === module.id);
          if (lastRoundModule?.data?.tidsKanaler) {
            moduleData.tidsKanaler = lastRoundModule.data.tidsKanaler;
          }
          logDebugSilently("Setting default tidskanaler from last round....");
        } else logDebugSilently("Setting default tidskanaler....");

        //return { ...moduleData, isRoundInitiated: true };
      }
      break;

    case ModuleTypeId.Lokaler:
      //Make sure there is at least one stickprov / zon
      if (!moduleData.stickprov) moduleData.stickprov = [];
      const zonesCoveredMap: any = {};
      moduleData.stickprov.forEach((sp: any) => {
        zonesCoveredMap[sp.zon] = true;
      });

      const zonModules = roundHandler
        .getRoundData()
        ?.data.modules.filter((m) => (m.family === ModuleTypeFamily.VärmeSekundar || m.family === ModuleTypeFamily.VärmeEl) && m.moduleTypeId !== ModuleTypeId.VarmeSekundarSaknas);
      const luftBurenVärmeModules = roundHandler
        .getRoundData()
        ?.data.modules.filter(
          (m) => (m.moduleTypeId === ModuleTypeId.VentilationFTX || m.moduleTypeId === ModuleTypeId.VentilationFT) && (m.data as IModuleVentilationFTXochFTConfig).ärHuvudsakligtVärmesystem === "Ja"
        );

      const allZoneInfos = (
        zonModules?.map((m) => {
          return { zon: m.data?.betjäningsområde, luftburen: false };
        }) || []
      ).concat(
        luftBurenVärmeModules?.map((m) => {
          return { zon: m.name, luftburen: true };
        }) || []
      );
      const existingZonesMap = allZoneInfos.reduce((acc, zonInfo) => {
        acc[zonInfo.zon] = true;
        return acc;
      }, {} as any);

      for (let i = 0; i < allZoneInfos.length; i++) {
        const zonInfo = allZoneInfos[i];
        if (!zonesCoveredMap[zonInfo.zon]) {
          moduleData.stickprov.push({
            zon: zonInfo.zon,
            uppmättTemperatur: undefined,
            uppmättTemperaturTilluft: undefined,
            värmeSystem: undefined,
            ventilationsSystem: undefined,
            komfortKylaAktiv: undefined,
            upplevdKomfort: undefined,
            ärLuftburenVärme: zonInfo.luftburen,
          });
        }
      }

      //Coerce Stickprovs to have the same structure
      moduleData.stickprov = moduleData.stickprov
        .filter((sp: any) => existingZonesMap[sp.zon])
        .map((sp: any) => {
          return {
            zon: sp.zon,
            uppmättTemperatur: sp.uppmättTemperatur,
            uppmättTemperaturTilluft: sp.uppmättTemperaturTilluft,
            värmeSystem: sp.värmeSystem,
            ventilationsSystem: sp.ärLuftburenVärme ? sp.zon : sp.ventilationsSystem,
            komfortKylaAktiv: sp.komfortKylaAktiv,
            upplevdKomfort: sp.upplevdKomfort,
            ärLuftburenVärme: sp.ärLuftburenVärme,
            inställningsvärdeTermostat: sp.inställningsvärdeTermostat,
          };
        });

      const dbg = 5;
      // const map: any = {};
      // const listVärmeSystem: string[] = [];
      // const listZon: string[] = [];
      // if (zonModules) {
      //   for (let i = 0; i < zonModules?.length; i++) {
      //     let mod = zonModules[i] as any;
      //     if (mod.data.betjäningsområde && mod.name) {
      //       map[mod.data.betjäningsområde] = mod.name;
      //       listVärmeSystem.push(mod.name);
      //       listZon.push(mod.data.betjäningsområde);
      //     }
      //   }
      // }

      // //Init stickprov
      // const initStickProv = listZon.map((zon) => {
      //   return {
      //     zon: zon,
      //     uppmättTemperatur: undefined,
      //     uppmättTemperaturTilluft: undefined,
      //     värmeSystem: map[zon],
      //     ventilationsSystem: undefined,
      //     komfortKylaAktiv: undefined,
      //     upplevdKomfort: undefined,
      //   };
      // });

      break;
    case ModuleTypeId.VarmeElAirotemper:
    case ModuleTypeId.VarmeElGolvvarme:
    case ModuleTypeId.VarmeElRadiatorer:
    case ModuleTypeId.VarmeElStralningsvarmare:
    case ModuleTypeId.VarmeElVarmepump:
      const lokalerModule = roundHandler.getRoundData()?.data.modules.find((m) => m.moduleTypeId === ModuleTypeId.Lokaler);
      const lokalerModuleData = lokalerModule?.data as IModuleLokaler;
      const stickprovThisModule = lokalerModuleData?.stickprov.filter((s) => s.värmeSystem === module.name);
      const inställningsvärdeGenomsnitt =
        (stickprovThisModule?.reduce((acc, s) => acc + (s.inställningsvärdeTermostat ? s.inställningsvärdeTermostat : 0), 0) || 0) / (stickprovThisModule?.length || 1);

      const inställningsvärdeGenomsnittOneDecimal = Math.round((inställningsvärdeGenomsnitt + Number.EPSILON) * 10) / 10;
      moduleData.termostatInställningsvärde = inställningsvärdeGenomsnittOneDecimal;
      break;
  }
};

const getModuleDefaultData = (id: ModuleTypeId, moduleData: any, roundHandler: IRoundHandler, module: IModuleRound) => {
  const lastRound = roundHandler.getLastFinishedRound();
  const lastRoundModule = lastRound?.data?.modules.find((m) => m.id === module.id);
  const lastRoundModuleData = lastRoundModule?.data;
  const initVärmePump = () => {
    if (lastRound) {
      const lastRoundModule = lastRound.data.modules.find((m) => m.id === module.id);

      if (lastRoundModule?.data) {
        moduleData[nameof<IModuleVärmePrimärPump>("ackumuleradeTimmar")] = lastRoundModule.data[nameof<IModuleVärmePrimärPump>("ackumuleradeTimmar")];
        moduleData[nameof<IModuleVärmePrimärPump>("ackumuleradeDrifttimmar")] = lastRoundModule.data[nameof<IModuleVärmePrimärPump>("ackumuleradeDrifttimmar")];
      }
    }

    //formHook.setValue(nameof<IModuleVärmePrimärPump>("ackumuleradeTimmar"), lastYear.ackumuleradeTimmar);
    //   }
    // }
    // if (!formValues.ackumuleradeDrifttimmar) {
    //   const lastYear = getLastFinishedRoundData<IModuleVärmePrimärPump>();
    //   if (lastYear && lastYear.ackumuleradeDrifttimmar) {
    //     formHook.setValue(nameof<IModuleVärmePrimärPump>("ackumuleradeDrifttimmar"), lastYear.ackumuleradeDrifttimmar);

    return { ...(moduleData || {}), isRoundInitiated: true };
  };
  switch (id) {
    // case ModuleTypeId.Byggnadsdata:
    //   return "Byggnadsdata & energistatistik";
    case ModuleTypeId.Lokaler:
      const zonModules = roundHandler.getRoundData()?.data.modules.filter((m) => m.family === ModuleTypeFamily.VärmeSekundar || m.family === ModuleTypeFamily.VärmeEl);

      const map: any = {};
      const listVärmeSystem: string[] = [];
      const listZon: string[] = [];
      if (zonModules) {
        for (let i = 0; i < zonModules?.length; i++) {
          let mod = zonModules[i] as any;
          if (mod.data.betjäningsområde && mod.name) {
            map[mod.data.betjäningsområde] = mod.name;
            listVärmeSystem.push(mod.name);
            listZon.push(mod.data.betjäningsområde);
          }
        }
      }

      //Init stickprov
      const initStickProv = listZon.map((zon) => {
        return {
          zon: zon,
          uppmättTemperatur: undefined,
          uppmättTemperaturTilluft: undefined,
          värmeSystem: map[zon],
          ventilationsSystem: undefined,
          komfortKylaAktiv: undefined,
          upplevdKomfort: undefined,
        };
      });
      const defaultWithStickprov = { ...(moduleData || {}), stickprov: initStickProv, isRoundInitiated: true };
      return defaultWithStickprov;

    // case ModuleTypeId.VarmePrimarFjarr:
    case ModuleTypeId.VarmePrimarPump:
      return initVärmePump();
      break;
    case ModuleTypeId.VarmeSekundarVatten:
      if (!moduleData.värmekurva) {
        const lastRound = roundHandler.getLastFinishedRound();
        if (lastRound?.data?.modules) {
          const thisModuleLastRound = lastRound.data.modules.find((m) => m.id === module.id);
          if (thisModuleLastRound) {
            moduleData.värmekurva = thisModuleLastRound.data?.värmekurva || [];
            logDebugSilently("Setting default värmekurva from last round....");
          }
        }
      }

      if (!moduleData.värmekurva) {
        moduleData.värmekurva = [
          { brytpunktUtetemp: 0, brytpunktFramledningstemp: 0 },
          { brytpunktUtetemp: 0, brytpunktFramledningstemp: 0 },
          { brytpunktUtetemp: 0, brytpunktFramledningstemp: 0 },
        ];
      }
      return { ...(moduleData || {}), isRoundInitiated: true };
    // case ModuleTypeId.VarmeElVarmepump:
    //   return "Värmepump Luft / Luft";
    // case ModuleTypeId.VarmeElStralningsvarmare:
    //   return "Strålningsvärmare";
    // case ModuleTypeId.VarmeElRadiatorer:
    //   return "Elradiatorer";
    // case ModuleTypeId.VarmeElAirotemper:
    //   return "EL airotemper";
    // case ModuleTypeId.VarmeElGolvvarme:
    //   return "Elektrisk golvvärme";
    // case ModuleTypeId.VentilationS:
    //   return "Ventilation (S-system)";
    // case ModuleTypeId.VentilationF:
    //   return "Ventilation (F-system)";
    // case ModuleTypeId.VentilationFT:
    //   return "Ventilation (FT-system)";

    case ModuleTypeId.VentilationF:
    case ModuleTypeId.VentilationFX:
    case ModuleTypeId.VentilationFT:
    case ModuleTypeId.VentilationFTX:
      if (id === ModuleTypeId.VentilationFX) moduleData = initVärmePump();

      if (!moduleData.tidsKanaler) {
        moduleData.tidsKanaler = {
          ventilation: { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] },
          närvaro: { monday: [], tuesday: [], wednesday: [], thursday: [], friday: [], saturday: [], sunday: [] },
        };
      }
      const lastRound = roundHandler.getLastFinishedRound();
      if (lastRound) {
        const lastRoundModule = lastRound.data.modules.find((m) => m.id === module.id);
        if (lastRoundModule?.data) {
          moduleData.tidsKanaler = lastRoundModule.data.tidsKanaler;
          moduleData.flödeUppgift = lastRoundModule.data.flödeUppgift;
          moduleData.flödeEnhet = lastRoundModule.data.flödeEnhet;
          moduleData.flödeTilluft = lastRoundModule.data.flödeTilluft;
          moduleData.flödeFrånluft = lastRoundModule.data.flödeFrånluft;
          moduleData.flödesKanalForm = lastRoundModule.data.flödesKanalForm;
          moduleData.flödeKanalstorlekBxH = lastRoundModule.data.flödeKanalstorlekBxH;
          moduleData.flödeFrånluftDiameter = Number(lastRoundModule.data.flödeFrånluftDiameter);

          moduleData.finnsOrsakerTillLångDrifttid = lastRoundModule.data.finnsOrsakerTillLångDrifttid;
          moduleData.nattkylaAktiverad = lastRoundModule.data.nattkylaAktiverad;
          moduleData.ventilationStoppadPeriodvis = lastRoundModule.data.ventilationStoppadPeriodvis;
          moduleData.antalVeckorVentilationStoppadPerÅr = lastRoundModule.data.antalVeckorVentilationStoppadPerÅr;
          moduleData.antalVeckorVentilationSkulleKunnaVaraStoppad = lastRoundModule.data.antalVeckorVentilationSkulleKunnaVaraStoppad;

          if (id === ModuleTypeId.VentilationFX) {
            moduleData.driftÅretRunt = lastRoundModule.data.driftÅretRunt;
            const bf = lastRoundModule.bfs?.BFL_J3_FX_F as IJ3_FX_F_BFData | undefined;
            if (bf?.data?.tidsKanaler && bf.data.harAngettNyaTidskanaler === "Ja") {
              moduleData.tidsKanaler = bf.data.tidsKanaler;
            }
          }

          if (id === ModuleTypeId.VentilationFTX) {
            const bf = lastRoundModule.bfs?.BFL_J3_FX_F as IJ3_FTX_FT_BFData | undefined;
            if (bf?.data?.tidsKanaler && bf.data.harAngettNyaTidskanaler === "Ja") {
              moduleData.tidsKanaler = bf.data.tidsKanaler;
            }
          }
        }
        logDebugSilently("Setting tidskanaler + flöde from last round....");
      } else logDebugSilently("Setting default tidskanaler....");

      return { ...(moduleData || {}), isRoundInitiated: true };

    // case ModuleTypeId.KylaVattenburet:
    //   return "Vattenburet kylsystem";
    // case ModuleTypeId.KylaMaskin:
    //   return "Kylmaskin";

    default:
      return { ...(moduleData || {}), isRoundInitiated: true };
  }
};
const ModuleRoundForm = ({ module, onCancel, onSubmit, onSaveDraft }: IProps) => {
  logDebugSilently("Render ModuleRoundForm:" + new Date());
  const roundHandler = useRound();
  const globalData = useGlobalData();
  const configurationData = module.data && module.data.isRoundInitiated ? module.data : getModuleDefaultData(module.moduleTypeId, module.data, roundHandler, module);
  validateModuleData(module.moduleTypeId, configurationData, roundHandler, module); //If changes have been made the application, that makes something not backward compatible - Here is the point to fix that!
  const formHook = useForm({
    defaultValues: configurationData,
  });
  const {
    handleSubmit,
    formState: { errors, isDirty },
  } = formHook;

  const onHelp = useCallback(() => {
    const isDirektEl = module.family === ModuleTypeFamily.VärmeEl;
    if (isDirektEl) {
      PubSub.publish("ShowHelp", "help_VarmeDirektEl_Rondering");
    } else {
      PubSub.publish("ShowHelp", "help_" + module.moduleTypeId + "_Rondering"); //);
    }
  }, [module.moduleTypeId, module.family]);

  const intOnSubmit = (data: any) => {
    onSubmit(data, isDirty);
  };

  const intOnSaveDraft = () => {
    const data = formHook.getValues();
    onSaveDraft(data, isDirty);
  };

  const displayName = getModuleTypeDisplayName(module.moduleTypeId);
  return (
    <>
      {/* <h2>
        Rondering {module.name}, {getModuleTypeDisplayName(module.moduleTypeId)}
      </h2> */}
      <form onSubmit={handleSubmit(intOnSubmit)} className="ModuleConfiguratorForm">
        <Card>
          <Card.Header>
            Rondering {module.name} {displayName !== "" && displayName !== module.name ? ", " + displayName : ""}
          </Card.Header>
          <Card.Body>
            <ModuleRoundEditor configurationData={configurationData} formHook={formHook} module={module} />
            <ValidationSummaryMessages errors={errors} />
          </Card.Body>
          <div className="flex-grow-1"></div>
          <Card.Footer>
            <div className="module">
              <button type="button" className="btn btn-info ms-2 me-5" onClick={onHelp}>
                Hjälp
              </button>
            </div>
            {!globalData.isOnline && (
              // globalData.isOnline
              <>
                <div
                  className="card-footer-info p-2 m-2  "
                  role="button"
                  style={{ backgroundColor: Colors.YellowMedium, color: Colors.GrayBlack, height: "100%", borderRadius: "5px" }}
                  onClick={() => PubSub.publish("ShowOfflineMessage", {})}
                >
                  <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                  <span className="d-none d-xl-block"> Du är offline - Ronderingen kan fortsättas men behöver sparas vid något senare tillfälle !</span>
                  <span className="d-none d-md-block d-xl-none"> Du är offline !</span>
                  <span className="d-block d-sm-block d-md-none"> Offline!</span>
                </div>
              </>
            )}
            <div className="footerGroup">
              <button type="button" className="btn btn-secondary ms-2" onClick={onCancel}>
                Avbryt
              </button>
              <button type="button" disabled={!globalData.isOnline} className="btn btn-secondary ms-2" onClick={intOnSaveDraft}>
                <FontAwesomeIcon className="me-2" icon={faSave}></FontAwesomeIcon>utkast
              </button>
              <button type="submit" className={"btn ms-2" + (globalData.isOnline ? " btn-success" : " btn-warning")}>
                Klar
              </button>
            </div>
          </Card.Footer>
        </Card>
      </form>
    </>
  );
};

export default ModuleRoundForm;
